import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Landmark } from 'lucide-react';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';
import { formatEUR } from '../lib/format';

/**
 * §5 item 7 — the LCR remittance list.
 *
 * WHAT THIS SCREEN IS, AND WHAT IT DELIBERATELY IS NOT
 * It is the list accounting keys into LCL: who to collect on a due date, how much each, and the
 * total to check the bank's confirmation against. It is NOT a CFONB file writer, and there is no
 * "send to the bank" button anywhere on it — the LCR process runs at LCL and works, and the
 * missing piece was never the file, it was MECADEPOT's numbers. Once the exact CFONB format LCL
 * accepts is known, a writer serialises exactly this payload; nothing here has to change.
 *
 * It is also strictly READ-ONLY, and that is a rule rather than a coincidence: "building a
 * remittance file never moves the credit limit — handing a file to the bank releases nothing".
 * Money collected by LCR is credited "sauf bonne fin", so the credit line is released later, from
 * the settlement screen, once the bank has confirmed. That is why nothing on this page posts.
 */

interface RemittanceLine {
  customerId: string;
  customerName: string;
  clientCode: string | null;
  invoiceId: string;
  invoiceNumber: string;
  recapReference: string | null;
  dueDate: string;
  amountDue: number;
}

interface Remittance {
  dueDate: string;
  channel: string;
  lines: RemittanceLine[];
  customerCount: number;
  total: number;
}

interface DueDateOption {
  dueDate: string;
  customerCount: number;
  total: number;
}

/** The instruments WE pull. A remittance is per-instrument — mixing them double-debits people. */
const CHANNELS = ['LCR', 'SEPA_SDD'] as const;

const day = (iso: string): string => new Date(iso).toLocaleDateString();

export default function Remittance() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [channel, setChannel] = useState<string>('LCR');
  const [dueDates, setDueDates] = useState<DueDateOption[] | null>(null);
  const [dueDate, setDueDate] = useState<string>('');
  const [remittance, setRemittance] = useState<Remittance | null>(null);
  const [loading, setLoading] = useState(false);

  const loadDueDates = useCallback(() => {
    setRemittance(null);
    setDueDate('');
    apiClient
      .get<{ data: DueDateOption[] }>(`/recap-invoices/remittance?channel=${channel}`)
      .then((r) => setDueDates(r.data.data ?? []))
      .catch((err) => {
        setDueDates([]);
        showApiError(err);
      });
    // showApiError is stable for the lifetime of the toast provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(loadDueDates, [loadDueDates]);

  const loadRemittance = (date: string) => {
    setDueDate(date);
    if (!date) {
      setRemittance(null);
      return;
    }
    setLoading(true);
    apiClient
      .get<{ data: Remittance }>(`/recap-invoices/remittance?channel=${channel}&dueDate=${date}`)
      .then((r) => setRemittance(r.data.data))
      .catch(showApiError)
      .finally(() => setLoading(false));
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('settlement.remittance.title')}
        description={t('settlement.remittance.description')}
      />

      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('settlement.remittance.instrument')}
          </span>
          <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {t(`settlement.remittance.channels.${c}`)}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('settlement.remittance.dueDate')}
          </span>
          <Select value={dueDate} onChange={(e) => loadRemittance(e.target.value)}>
            <option value="">{t('settlement.remittance.selectDueDate')}</option>
            {(dueDates ?? []).map((d) => (
              <option key={d.dueDate} value={d.dueDate}>
                {t('settlement.remittance.dueDateOption', {
                  date: day(d.dueDate),
                  count: d.customerCount,
                  total: formatEUR(d.total),
                })}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {dueDates !== null && dueDates.length === 0 && (
        <p className="rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
          {t('settlement.remittance.nothingToCollect', {
            channel: t(`settlement.remittance.channels.${channel}`, { defaultValue: channel }),
          })}
        </p>
      )}

      {loading && (
        <p className="text-sm text-muted-foreground">{t('settlement.remittance.loading')}</p>
      )}

      {remittance && (
        <>
          <div className="flex flex-wrap items-center gap-6 rounded-md border bg-muted/40 px-4 py-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t('settlement.remittance.dueDate')}
              </div>
              <div className="text-lg font-semibold">{day(remittance.dueDate)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t('settlement.remittance.customers')}
              </div>
              <div className="text-lg font-semibold">{remittance.customerCount}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t('settlement.remittance.totalToCollect')}
              </div>
              <div className="text-lg font-semibold tabular-nums">
                {formatEUR(remittance.total)}
              </div>
            </div>
            <p className="ml-auto max-w-md text-xs text-muted-foreground">
              <Landmark className="mr-1 inline h-3.5 w-3.5" />
              {t('settlement.remittance.checkNote')}
            </p>
          </div>

          {remittance.lines.length === 0 ? (
            <p className="rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
              {t('settlement.remittance.nothingLeft')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left font-medium">
                      {t('settlement.remittance.columns.code')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('settlement.remittance.columns.customer')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('settlement.remittance.columns.invoice')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('settlement.remittance.columns.reference')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('settlement.remittance.columns.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {remittance.lines.map((l) => (
                    <tr key={l.invoiceId} className="border-b last:border-b-0">
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">
                        {l.clientCode ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 font-medium">{l.customerName}</td>
                      <td className="px-3 py-1.5">{l.invoiceNumber}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">
                        {l.recapReference ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {formatEUR(l.amountDue)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 bg-muted/30 font-semibold">
                    <td className="px-3 py-2" colSpan={4}>
                      {t('settlement.remittance.total')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatEUR(remittance.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
